import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import AuctionCard from "@/components/AuctionCard";
import { ArrowRight, Flame, Sparkles, Timer } from "lucide-react";

export default function Home() {
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/auctions", { params: { status: "live", limit: 25 } });
      setAuctions(data.auctions);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="grid-bg min-h-screen">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-10">
        <div className="grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <div className="chip chip-live mb-6" data-testid="hero-badge">
              <Flame size={12} /> Live now — bidding is open
            </div>
            <h1 className="font-display font-extrabold tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.9] mb-6">
              Win premium gear<br />
              <span className="text-[#FBBF24]">one penny</span> at a time.
            </h1>
            <p className="text-zinc-400 text-base sm:text-lg max-w-xl leading-relaxed">
              Every bid raises the price by <span className="font-mono-nums text-white">$0.01</span> and
              resets the timer. The auction closes <span className="text-[#FBBF24] font-mono-nums">only</span> once
              platform profit hits <span className="text-[#FBBF24] font-mono-nums">$500</span> — and
              the last bidder standing takes the prize home.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <Link to="/register" data-testid="hero-cta-signup" className="btn-bid text-sm !px-6">
                Get 20 free tokens →
              </Link>
              <a href="#auctions" className="btn-outline text-sm">Browse live auctions</a>
            </div>
          </div>

          <div className="md:col-span-4">
            <div className="card-surface p-6 space-y-5">
              <StatRow icon={<Timer size={18} />} label="Bid cost" value="$0.50" />
              <StatRow icon={<Sparkles size={18} />} label="Price bump / bid" value="$0.01" />
              <StatRow icon={<Flame size={18} />} label="Timer resets to" value="30 s" />
              <StatRow icon={<ArrowRight size={18} />} label="Closes only at" value="$500 profit" />
            </div>
          </div>
        </div>
      </section>

      {/* Auctions Grid */}
      <section id="auctions" className="max-w-7xl mx-auto px-6 pb-24">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tighter">
              Live auctions
            </h2>
            <p className="text-zinc-500 text-sm mt-1">
              Showing up to 25 live auctions. Updated every few seconds — move fast.
            </p>
          </div>
          <Link to="/winners" className="btn-outline text-sm hidden sm:inline-flex">
            See past winners
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="card-surface p-5 h-[420px] animate-pulse">
                <div className="aspect-square bg-white/5 rounded-lg mb-4"></div>
                <div className="h-4 bg-white/5 rounded mb-2"></div>
                <div className="h-8 bg-white/5 rounded"></div>
              </div>
            ))}
          </div>
        ) : auctions.length === 0 ? (
          <div className="card-surface p-12 text-center">
            <p className="text-zinc-400">No live auctions right now. Check back soon.</p>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6"
            data-testid="auctions-grid"
          >
            {auctions.map((a) => (
              <AuctionCard key={a.id} auction={a} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function StatRow({ icon, label, value }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3 text-zinc-400 text-sm">
        <span className="text-[#FBBF24]">{icon}</span>
        {label}
      </div>
      <div className="font-mono-nums font-bold">{value}</div>
    </div>
  );
}
